import { lineIdOf } from '@src/editor/dubbing/DubCoverageService';
import { dropSuperseded } from '@src/editor/dubbing/DubPlacementService';

import type { DubManifestDTO } from '@src/repositories/DubbingRepository';
import type { TranslationSegment } from '@src/types.ts/translation';

/**
 * PLAT-11678 — what an automatic re-dub should do about the segments a
 * translation edit just touched. Pure: no React, no MobX, no IO. The hook that
 * owns the timing (`useAutoRedubOnCommit`) gathers the inputs and applies the
 * answer.
 */

/**
 * The translation segments (PLAT-10260 `TranslationStore.getSegments`) that
 * carry any of these source-element ids. The edit surfaces all report the
 * source ids they covered (`onSpanConfirmed`, `onPhraseEditSaved`), never a
 * segment, so this is the one containment rule every commit path shares —
 * the same rule `useSpanReplace.reDubSegmentIds` applies to a pending span.
 */
export function segmentIdsForElementIds(
  segments: readonly TranslationSegment[],
  elementIds: readonly string[],
): string[] {
  if (elementIds.length === 0) return [];
  const wanted = new Set(elementIds);
  const hits: string[] = [];
  for (const segment of segments) {
    const touched = segment.fragments.some(fragment =>
      (fragment.originalElementIds ?? []).some(id => wanted.has(id)),
    );
    if (touched) hits.push(segment.id);
  }
  return hits;
}

/**
 * The id the dub engine keys a translation segment's recording on — the SAME id
 * `DubCoverageService.lineIdOf` / `rebuildSegmentTextsNow` file the line under,
 * so the plan, the manifest and the rebuilt text all agree on one key.
 *
 * An ordinary sentence's line is its sentence anchor: `parentId`, else
 * `p_<firstElementId>` (mirroring `anchorOf`). It is NOT `segment.id`:
 * `getSegments` keys an unsplit fragment by its BARE element id, but the manifest
 * and the rebuilt text key that same line `p_<elementId>`, so returning
 * `segment.id` here would look up a recording the manifest never carries.
 *
 * A span replacement (`o_`) has no recording of its own: the backend splices it
 * into its host sentence and records ONE line under the host's `p_` anchor (see
 * `lineIdOf`), so the host is what gets re-dubbed. `sentenceByKey` is that
 * service's element → sentence-anchor index, built from all base fragments
 * including superseded ones so a replacement still finds its home. A replacement
 * that names no source ids has no line to resolve; `lineIdOf` falls it back to
 * the record's own id rather than letting it vanish.
 */
export function dubLineIdForSegment(
  segment: TranslationSegment,
  sentenceByKey: ReadonlyMap<string, string>,
): string {
  const firstKey = segment.fragments
    .flatMap(fragment => fragment.originalElementIds ?? [])
    .find((key): key is string => key !== undefined);
  if (segment.id.startsWith('o_'))
    return lineIdOf(segment.id, firstKey, sentenceByKey);
  const parentId = segment.fragments[0]?.parentId;
  if (parentId) return parentId;
  return firstKey !== undefined ? `p_${firstKey}` : segment.id;
}

export interface AutoRedubPlanInput {
  /** Dub line ids (`p_…`) whose translation was just saved. */
  pendingLineIds: readonly string[];
  /** The language's dub manifest as the server holds it now; `null` = no dub. */
  manifest: DubManifestDTO | null;
  /**
   * Each line's translated text as it reads NOW, rebuilt the way the backend
   * joins it (`DubCoverageService.rebuildSegmentTextsNow`), keyed by line id.
   */
  textNowByLineId: ReadonlyMap<string, string>;
  /**
   * Whether `dubbedFrom` and `translationNow` differ in a way that changes the
   * spoken line — `DubCoverageService.dubTextChanged`, so this plan and the
   * out-of-date warning can never disagree about the same edit. Receives the
   * line id because the comparison relaxes to word order when that line's
   * order could not be rebuilt.
   */
  changed: (
    lineId: string,
    dubbedFrom: string,
    translationNow: string,
  ) => boolean;
  /**
   * Lines to re-dub even when their text still matches the recording — the
   * reviewer's Retry after a failed run, where the recording is the OLD one.
   */
  forceLineIds?: readonly string[];
}

export interface AutoRedubPlan {
  /** Lines to send to the engine, in the order they were pending. */
  lineIds: string[];
  /** Pending lines whose recording already matches the saved text (DE1 hit). */
  unchanged: string[];
  /** Pending lines the manifest has no live recording for — a gap, not ours. */
  notDubbed: string[];
  /** Pending lines whose recording names no AI voice — nothing to re-dub with. */
  noVoice: string[];
  /**
   * `spk_X → aiVoiceId` for exactly the speakers of `lineIds`, read off the
   * recordings being replaced — the only record of what a line was voiced with
   * (the run that made it kept no speaker → voice map).
   */
  voiceBySpeaker: Record<string, string>;
}

/**
 * Decide which of the just-saved lines actually need new audio.
 *
 * DE1 on the client: a line whose rebuilt text still matches the words its
 * recording was voiced from is left alone (a revert to a prior value, or a save
 * that changed nothing spoken). Everything that still differs goes to the
 * engine as one scoped run, voiced with the same voice as before.
 */
export function planAutoRedub(input: AutoRedubPlanInput): AutoRedubPlan {
  const { pendingLineIds, manifest, textNowByLineId, changed } = input;
  const force = new Set(input.forceLineIds ?? []);
  const live = manifest ? dropSuperseded(manifest.segments ?? []).live : [];
  const recordingByLineId = new Map(live.map(s => [s.segmentId, s]));

  const plan: AutoRedubPlan = {
    lineIds: [],
    unchanged: [],
    notDubbed: [],
    noVoice: [],
    voiceBySpeaker: {},
  };
  const seen = new Set<string>();
  for (const lineId of pendingLineIds) {
    if (seen.has(lineId)) continue;
    seen.add(lineId);
    const recording = recordingByLineId.get(lineId);
    if (!recording) {
      plan.notDubbed.push(lineId);
      continue;
    }
    const textNow = textNowByLineId.get(lineId);
    if (
      !force.has(lineId) &&
      textNow !== undefined &&
      !changed(lineId, recording.sourceTranslationSnapshot, textNow)
    ) {
      plan.unchanged.push(lineId);
      continue;
    }
    if (!recording.aiVoiceId) {
      plan.noVoice.push(lineId);
      continue;
    }
    plan.lineIds.push(lineId);
    plan.voiceBySpeaker[recording.speaker] = recording.aiVoiceId;
  }
  return plan;
}
